import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateDashboardInput,
  DashboardListResponseData,
  DashboardResponseData,
  DeleteDashboardResponseData,
  UpdateDashboardInput,
} from "@veritab/api-contracts";
import axios, { AxiosError } from "axios";

import { ApiError } from "@/shared/lib/errors";

import { dashboardKeys } from "./keys";

// API client for dashboards
export const dashboardApi = axios.create({
  baseURL: `${import.meta.env.VITE_CORE_API_URL}/dashboards`,
});

// API functions
export const getDashboards = async () => {
  try {
    const response = await dashboardApi.get("/", {
      withCredentials: true,
    });
    const responseData = response.data as DashboardListResponseData;
    if (!responseData.success) {
      throw new Error(responseData.message);
    }
    return responseData.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new ApiError(error);
    }
    throw new Error("An unknown error occurred");
  }
};

export const getDashboardBySlug = async (slug: string) => {
  try {
    const response = await dashboardApi.get(`/${slug}`, {
      withCredentials: true,
    });
    const responseData = response.data as DashboardResponseData;
    if (!responseData.success) {
      throw new Error(responseData.message);
    }
    return responseData.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new ApiError(error);
    }
    throw new Error("An unknown error occurred");
  }
};

export const getDefaultDashboard = async () => {
  try {
    const response = await dashboardApi.get("/default", {
      withCredentials: true,
    });
    const responseData = response.data as DashboardResponseData;
    if (!responseData.success) {
      throw new Error(responseData.message);
    }
    return responseData.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new ApiError(error);
    }
    throw new Error("An unknown error occurred");
  }
};

export const createDashboard = async (input: CreateDashboardInput) => {
  try {
    const response = await dashboardApi.post("/", input, {
      withCredentials: true,
    });
    const responseData = response.data as DashboardResponseData;
    if (!responseData.success) {
      throw new Error(responseData.message);
    }
    return responseData.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new ApiError(error);
    }
    throw new Error("An unknown error occurred");
  }
};

// React Query hooks
export function useDashboardsQuery() {
  return useQuery({
    queryKey: dashboardKeys.list(),
    queryFn: getDashboards,
  });
}

export function useDashboardBySlugQuery(slug: string) {
  return useQuery({
    queryKey: dashboardKeys.detail(slug),
    queryFn: () => getDashboardBySlug(slug),
    enabled: !!slug,
  });
}

export function useDefaultDashboardQuery() {
  return useQuery({
    queryKey: dashboardKeys.default(),
    queryFn: getDefaultDashboard,
  });
}

export function useCreateDashboardMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDashboard,
    onSuccess: async () => {
      // Invalidate dashboard list to refetch
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
    },
  });
}

// Update dashboard API function
export const updateDashboard = async (id: string, input: UpdateDashboardInput) => {
  try {
    const response = await dashboardApi.put(`/${id}`, input, {
      withCredentials: true,
    });
    const responseData = response.data as DashboardResponseData;
    if (!responseData.success) {
      throw new Error(responseData.message);
    }
    return responseData.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new ApiError(error);
    }
    throw new Error("An unknown error occurred");
  }
};

// Delete dashboard API function
export const deleteDashboard = async (id: string) => {
  try {
    const response = await dashboardApi.delete(`/${id}`, {
      withCredentials: true,
    });
    const responseData = response.data as DeleteDashboardResponseData;
    if (!responseData.success) {
      throw new Error(responseData.message);
    }
    return responseData.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new ApiError(error);
    }
    throw new Error("An unknown error occurred");
  }
};

export function useUpdateDashboardMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDashboardInput }) =>
      updateDashboard(id, data),
    onSuccess: async () => {
      // Invalidate dashboard list and default dashboard to refetch
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.default() });
    },
  });
}

export function useDeleteDashboardMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDashboard(id),
    onSuccess: async () => {
      // Invalidate dashboard list and default dashboard to refetch
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.default() });
    },
  });
}
